import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, FileText, Link2, Play } from 'lucide-react';
import api from '../utils/api';
import { extractUrls, videoShape } from '../utils/links';

/**
 * 외부 링크 카드 — 서버(/api/seo/og-preview)가 정리한 메타데이터로 그린다.
 *  - 동영상: 썸네일 카드 → 누르면 그 자리에서 플레이어 재생(피드 스크롤 중엔 iframe을 싣지 않는다)
 *  - 논문: 학술지·연도·저자
 *  - 그 밖: 썸네일 + 제목 + 사이트. 메타를 못 읽어도 같은 모양의 도메인 카드로 보인다.
 * 응답 전에는 같은 크기의 자리표시자를 그려, 카드가 늦게 도착해도 글이 밀리지 않게 한다.
 */
export interface LinkPreviewData {
  kind: 'video' | 'paper' | 'link';
  url: string;
  domain: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  image: string | null;
  provider?: 'youtube' | 'vimeo' | 'pubmed' | 'pmc' | 'doi';
  video?: { id: string; embedUrl: string; vertical: boolean };
  paper?: { journal: string | null; year: string | null; authors: string | null; pmid?: string; doi?: string };
}

// 피드와 상세가 같은 링크를 다시 그려도 한 번만 묻는다(서버도 24시간 캐시)
const previewCache = new Map<string, Promise<LinkPreviewData | null>>();
function loadPreview(url: string): Promise<LinkPreviewData | null> {
  let pending = previewCache.get(url);
  if (!pending) {
    pending = api
      .get('/seo/og-preview', { params: { url } })
      .then((r) => r.data as LinkPreviewData)
      .catch(() => null);
    previewCache.set(url, pending);
  }
  return pending;
}

// 서버가 준 프록시 경로(/api/...)를 API 출처 기준으로 — 로컬 개발은 API가 다른 포트에 있다
const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
const resolveImage = (src: string | null) => (src && src.startsWith('/api/') ? `${API_ORIGIN}${src}` : src);

const stop = (e: React.SyntheticEvent) => e.stopPropagation(); // 피드 카드 클릭(상세 열기)과 분리

function hostLabel(url: string): string {
  try {
    const u = new URL(url);
    const path = decodeURIComponent(u.pathname + u.search);
    return path.length > 1 ? `${u.hostname.replace(/^www\./, '')}${path}` : u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const VideoCard: React.FC<{ data: LinkPreviewData }> = ({ data }) => {
  const [playing, setPlaying] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const video = data.video!;
  const providerName = data.provider === 'vimeo' ? 'Vimeo' : 'YouTube';
  const title = data.title || `${providerName} 동영상`;
  return (
    <div onClick={stop}>
      <div
        className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-900 ${
          video.vertical ? 'aspect-[9/16] w-full max-w-[280px]' : 'aspect-video w-full'
        }`}
      >
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={video.embedUrl}
            title={title}
            // 사이트는 Referrer-Policy: no-referrer — 그대로면 YouTube가 출처를 확인 못 해 "오류 153"을 낸다.
            // 이 iframe만 출처(https://pulse.ai.kr)를 보내고, 글 경로는 보내지 않는다.
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full text-left"
            aria-label={`${title} 재생`}
          >
            {data.image && !imgFailed && (
              <img
                src={resolveImage(data.image) || undefined}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setImgFailed(true)}
                className="h-full w-full object-cover"
              />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/70 transition-colors group-hover:bg-black/85">
                <Play className="ml-0.5 h-6 w-6 fill-current text-white" />
              </span>
            </span>
            <span className="absolute bottom-3 left-3 right-3 text-white">
              <span className="block text-sm font-semibold leading-snug line-clamp-2">{title}</span>
              <span className="mt-0.5 block text-xs text-white/80">
                {[data.description, providerName].filter(Boolean).join(' · ')}
              </span>
            </span>
          </button>
        )}
      </div>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1.5 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
      >
        {providerName}에서 보기 <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};

const PaperCard: React.FC<{ data: LinkPreviewData; compact: boolean }> = ({ data, compact }) => {
  const p = data.paper || { journal: null, year: null, authors: null };
  const source = [data.provider === 'pubmed' ? 'PubMed' : data.provider === 'pmc' ? 'PMC' : 'DOI', p.journal, p.year]
    .filter(Boolean)
    .join(' · ');
  return (
    <a
      href={data.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stop}
      className="block rounded-lg border border-gray-200 bg-white p-3 transition-colors hover:bg-gray-50 sm:p-4"
    >
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        <FileText className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{source}</span>
      </span>
      <span className={`mt-1 block text-sm font-semibold leading-snug text-gray-900 ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>
        {data.title || hostLabel(data.url)}
      </span>
      {p.authors && <span className="mt-1 block truncate text-xs text-gray-500">{p.authors}</span>}
      {!compact && (p.pmid || p.doi) && (
        <span className="mt-1 block truncate text-xs text-gray-400">
          {[p.pmid && `PMID ${p.pmid}`, p.doi && `DOI ${p.doi}`].filter(Boolean).join(' · ')}
        </span>
      )}
    </a>
  );
};

/** 일반 링크 — 메타가 없어도 같은 모양(왼쪽 정사각 썸네일 자리 + 오른쪽 글)으로 그린다. */
const LinkCard: React.FC<{ url: string; data: LinkPreviewData | null; compact: boolean }> = ({ url, data, compact }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const image = data?.image && !imgFailed ? resolveImage(data.image) : null;
  const domain = data?.domain || hostLabel(url).split('/')[0];
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stop}
      className={`flex overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:bg-gray-50 ${
        compact ? 'h-24' : 'h-24 sm:h-28'
      }`}
    >
      <span
        className={`flex flex-shrink-0 items-center justify-center border-r border-gray-100 bg-gray-50 ${
          compact ? 'w-24' : 'w-24 sm:w-28'
        }`}
      >
        {image ? (
          <img
            src={image}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <Link2 className="h-6 w-6 text-gray-300" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2">
        <span className="text-sm font-semibold leading-snug text-gray-900 line-clamp-2">
          {data?.title || hostLabel(url)}
        </span>
        {data?.description && (
          <span className={`mt-0.5 text-xs text-gray-500 ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}>
            {data.description}
          </span>
        )}
        <span className="mt-1 flex items-center gap-1 truncate text-xs text-gray-400">
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{data?.siteName && data.siteName.length <= 30 ? data.siteName : domain}</span>
        </span>
      </span>
    </a>
  );
};

const LinkPreviewCard: React.FC<{ url: string; compact: boolean }> = ({ url, compact }) => {
  const holder = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [data, setData] = useState<LinkPreviewData | null | undefined>(undefined); // undefined = 아직 모름

  // 화면 가까이 왔을 때만 요청한다 — 피드 첫 화면에서 링크 수십 개를 한꺼번에 묻지 않게
  useEffect(() => {
    const el = holder.current;
    if (near || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return;
    let alive = true;
    loadPreview(url).then((d) => {
      if (alive) setData(d);
    });
    return () => {
      alive = false;
    };
  }, [near, url]);

  if (data === undefined) {
    const shape = videoShape(url);
    return (
      <div
        ref={holder}
        aria-hidden="true"
        className={`animate-pulse rounded-lg border border-gray-200 bg-gray-50 ${
          shape === 'tall'
            ? 'aspect-[9/16] w-full max-w-[280px]'
            : shape === 'wide'
              ? 'aspect-video w-full'
              : compact
                ? 'h-24'
                : 'h-24 sm:h-28'
        }`}
      />
    );
  }
  if (data?.kind === 'video' && data.video) return <VideoCard data={data} />;
  if (data?.kind === 'paper') return <PaperCard data={data} compact={compact} />;
  return <LinkCard url={url} data={data} compact={compact} />;
};

interface LinkPreviewListProps {
  content: string;
  /** 피드 카드는 1개, 상세는 3개까지 */
  max?: number;
  compact?: boolean;
  className?: string;
}

export const LinkPreviewList: React.FC<LinkPreviewListProps> = ({ content, max = 3, compact = false, className = '' }) => {
  const urls = useMemo(() => extractUrls(content).slice(0, max), [content, max]);
  if (urls.length === 0) return null;
  return (
    <div className={`space-y-3 ${className}`}>
      {urls.map((url) => (
        <LinkPreviewCard key={url} url={url} compact={compact} />
      ))}
    </div>
  );
};
